import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import {
  isMembershipResponseArray,
  isTenantResponse,
  isTenantResponseArray,
  type MembershipResponse,
  type TenantResponse,
} from '@/api/responses'
import type { components } from '@/api/schema'

export type CreateTenantPayload = components['schemas']['CreateTenantRequest']

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

export async function listMyTenants(): Promise<readonly TenantResponse[]> {
  const result = await apiClient.GET('/api/v1/tenants')

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  const body: unknown = result.data

  if (!isTenantResponseArray(body)) {
    throw unexpectedFormatError(result.response.status)
  }

  return body
}

export async function createTenant(payload: CreateTenantPayload): Promise<TenantResponse> {
  const result = await apiClient.POST('/api/v1/tenants', { body: payload })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  const body: unknown = result.data

  if (!isTenantResponse(body)) {
    throw unexpectedFormatError(result.response.status)
  }

  return body
}

/**
 * Exclui a organizacao (danger zone). Owner-only no backend (capability `tenant.delete`); o soft-delete
 * bloqueia o acesso imediatamente. 204 em sucesso; erro tipado (403 quando nao e o Owner, etc.).
 */
export async function deleteTenant(tenantId: string): Promise<void> {
  const result = await apiClient.DELETE('/api/v1/tenants/{tenantId}', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/**
 * Lista os membros do tenant. Retorna `null` quando o usuario nao tem a capability
 * `team.manage` (403) para que quem chamar possa ocultar o bloco silenciosamente,
 * em vez de mostrar um erro para uma permissao que o usuario nunca teve.
 */
export async function listTenantMembers(
  tenantId: string,
): Promise<readonly MembershipResponse[] | null> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/members', {
    params: { path: { tenantId } },
  })

  if (result.response.status === 403) {
    return null
  }

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  const body: unknown = result.data

  if (!isMembershipResponseArray(body)) {
    throw unexpectedFormatError(result.response.status)
  }

  return body
}
